"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldDescription,
} from "@/components/ui/field";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TriangleAlertIcon } from "lucide-react";
import { useDraftResumeStore } from "@/stores/draft-resume-store";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, AlertCircle, CheckCircle, Loader2, Info } from "lucide-react";
import {
  saveDraftData,
  getVehicleData,
} from "@/app/lib/actions/cat/vehicle-identification-step";
import { formatProductionWeekLabel } from "@/lib/cat/format-production-week";
import { RETAIL_DRAFT_TOTAL_STEPS } from "@/lib/cat/draft-step-indices";
import { kvpsBrandLetterToDisplayLabel, Kvps } from "@/lib/kvps";
import { useRetailVehicleAssignmentStore } from "@/stores/mulesoft-vehicle-data-Store";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";

interface VehicleIdentificationProps {
  onCanProceedChange?: (canProceed: boolean) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
  currentKvps?: Kvps | null;
}

export default function VehicleIdentification({
  onCanProceedChange,
  onBeforeNextChange,
  currentKvps,
}: VehicleIdentificationProps) {
  const [chassisNumber, setChassisNumber] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderYear, setOrderYear] = useState("");

  const [loading, setLoading] = useState(false);
  const [linkedExistingDraft, setLinkedExistingDraft] = useState<{
    id: number;
    currentStepIndex: number;
  } | null>(null);
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const [vehicleFound, setVehicleFound] = useState(() => !!currentDraft);
  const [error, setError] = useState("");
  const muleSoftVehicleData = useRetailVehicleAssignmentStore(
    (state) => state.muleSoftVehicleData,
  );
  const setMuleSoftVehicleData = useRetailVehicleAssignmentStore(
    (state) => state.setMuleSoftVehicleData,
  );
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );
  const inconsistencies = useDraftResumeStore((state) => state.inconsistencies);
  const resumeInconsistencies = useMemo(
    () =>
      inconsistencies.filter(
        (item) => item.stepId === "vehicle_identification",
      ),
    [inconsistencies],
  );

  useEffect(() => {
    onCanProceedChange?.(vehicleFound);
  }, [vehicleFound, onCanProceedChange]);

  const handleBeforeNext = useCallback(async () => {
    if (!muleSoftVehicleData) {
      return false;
    }

    const result = await saveDraftData({
      ...muleSoftVehicleData,
      id: currentDraft?.id,
    });

    if (!result.success) {
      setError(
        result.message ??
          "Une erreur est survenue lors de l'enregistrement du véhicule.",
      );
      return false;
    }

    if (!result.data) {
      setError(
        "Aucune donnée véhicule n'a été retournée après l'enregistrement.",
      );
      return false;
    }

    setCurrentDraft(result.data);

    return true;
  }, [currentDraft?.id, muleSoftVehicleData, setCurrentDraft]);

  useEffect(() => {
    onBeforeNextChange?.(handleBeforeNext);
    return () => onBeforeNextChange?.(null);
  }, [handleBeforeNext, onBeforeNextChange]);

  const handleSearch = async () => {
    if (!chassisNumber && (!orderNumber || !orderYear)) {
      setError(
        "Veuillez saisir un numéro de châssis ou un numéro commande ainsi que l'année de production.",
      );
      return;
    }

    if (chassisNumber && chassisNumber.length < 17) {
      setError("Le VIN doit obligatoirement contenir 17 caractères.");
      return;
    }

    setLoading(true);
    setError("");
    setVehicleFound(false);
    setLinkedExistingDraft(null);
    setMuleSoftVehicleData(null);

    const searchData = chassisNumber
      ? { chassisNumber }
      : { orderNumber, orderYear };
    const searchVehicleResponse = await getVehicleData(searchData);

    if (!searchVehicleResponse.success) {
      setLoading(false);
      setError(searchVehicleResponse.message || "");
      return;
    }

    setLoading(false);
    if (!searchVehicleResponse.data) {
      setError("Aucune donnée véhicule n'a été retournée.");
      return;
    }

    setVehicleFound(true);
    setMuleSoftVehicleData(searchVehicleResponse.data);
    setLinkedExistingDraft(searchVehicleResponse.data.existingDraft);
    setError("");
  };

  return (
    <div className="space-y-6">
      {resumeInconsistencies.map((item, index) => (
        <Alert key={`${item.message}-${index}`} variant="destructive">
          <TriangleAlertIcon className="size-4" />
          <AlertDescription>{item.message}</AlertDescription>
        </Alert>
      ))}

      <Card>
        <CardHeader className="space-y-3 pb-2">
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-[#005EA8]" />
            <span>Identification du Véhicule</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p>
                Recherchez le véhicule par son VIN (17 caractères) ou son numéro
                et année de commande. La marque sera déterminée automatiquement
                selon le contexte KVPS{" "}
                <strong>{currentKvps?.identifier}</strong>.
              </p>
            </AlertDescription>
          </Alert>

          <FieldGroup className="grid grid-cols-8 gap-4">
            <Field className="col-span-3" data-invalid={false}>
              <FieldLabel htmlFor="chassisNumber">
                Numéro de Châssis (VIN)
              </FieldLabel>
              <Input
                id="chassisNumber"
                placeholder="Saisir 17 caractères..."
                value={chassisNumber}
                onChange={(e) => setChassisNumber(e.target.value.toUpperCase())}
                maxLength={17}
                disabled={!!orderNumber || !!orderYear}
              />
              <FieldDescription>
                {"Numéro d'identification du véhicule"}
              </FieldDescription>
            </Field>
            <Field className="col-span-3" data-invalid={false}>
              <FieldLabel htmlFor="orderNumber">Numéro de Commande</FieldLabel>
              <Input
                id="orderNumber"
                placeholder="Saisir 6 caractères..."
                value={orderNumber}
                maxLength={6}
                onChange={(e) => setOrderNumber(e.target.value)}
                disabled={!!chassisNumber}
              />
              <FieldDescription>Référence commande usine</FieldDescription>
            </Field>
            <Field className="col-span-2" data-invalid={false}>
              <FieldLabel htmlFor="orderDateYear">Année</FieldLabel>
              <Input
                id="orderDateYear"
                placeholder="Saisir 4 chiffres..."
                value={orderYear}
                maxLength={4}
                onChange={(e) => setOrderYear(e.target.value)}
                disabled={!!chassisNumber}
              />
              <FieldDescription>Année commande usine</FieldDescription>
            </Field>
          </FieldGroup>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={() => handleSearch()}
            disabled={
              (!chassisNumber && (!orderNumber || !orderYear)) || loading
            }
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recherche en cours...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Rechercher Véhicule
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Skeleton Loader */}
      {loading && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <Skeleton className="h-6 w-1/3" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle Found */}
      {vehicleFound && muleSoftVehicleData && !loading && (
        <>
          {linkedExistingDraft ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Ce véhicule est déjà lié au brouillon{" "}
                <strong>DRAFT-{linkedExistingDraft.id}</strong>. En poursuivant,
                vous reprendrez ce brouillon en cours (progression :{" "}
                {Math.min(
                  linkedExistingDraft.currentStepIndex,
                  RETAIL_DRAFT_TOTAL_STEPS,
                )}{" "}
                / {RETAIL_DRAFT_TOTAL_STEPS}).
              </AlertDescription>
            </Alert>
          ) : null}

          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-green-800">
                <CheckCircle className="h-5 w-5" />
                <span>Véhicule Trouvé</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900">
                    Détails du Véhicule
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">VIN :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.vin}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Marque :</span>
                      <Badge className="bg-[#005EA8]">
                        {kvpsBrandLetterToDisplayLabel(
                          muleSoftVehicleData.brand,
                        ) ?? muleSoftVehicleData.brand}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code modèle :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.modelCode}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Libellé modèle :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.modelDescription}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">année modèle :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.modelYear}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code peinture :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.paintCode}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Libellé peinture :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.paintDescription}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code intérieur :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.interiorCode}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Libellé intérieur :</span>
                      <span className="font-medium">
                        {muleSoftVehicleData.interiorDescription}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Options :</span>
                      <span className="max-w-[250px] overflow-hidden font-medium text-ellipsis">
                        {muleSoftVehicleData.options
                          .map((option) => option?.prCoding?.prNumber || "")
                          .join(", ")}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900">
                    Statut & Production
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Production :</span>
                      <span className="font-medium">
                        {formatProductionWeekLabel(
                          muleSoftVehicleData.productionDate,
                        ) ?? muleSoftVehicleData.productionDate}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
